// ────────────────────────────────────────────────────────────────────
// Auth helpers — JWT login mutation + RequireAuth route guard.
// ────────────────────────────────────────────────────────────────────

import { useMutation } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import type { JSX, ReactNode } from "react";
import {
  apiClient, errMsg, getToken, setToken, setCachedUser, clearToken,
} from "./api";

/** Login mutation — handles token + user caching. */
export function useLogin() {
  return useMutation({
    mutationFn: async (vars: { username: string; password: string }) => {
      const data = await apiClient.login(vars.username, vars.password);
      setToken(data.access_token);
      setCachedUser(data.user);
      return data;
    },
  });
}

/** Logout — clear storage + return to login. */
export function logout(): void {
  clearToken();
  window.location.href = "/login";
}

/** Route guard — redirect unauthenticated users to /login, preserving
 *  the attempted path as ?next= so we bounce back after login. */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const token = getToken();
  if (!token) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Role-based guard. Customer-facing pages don't need this; Studio /
 *  internal-tool routes use it to block non-staff users.
 *
 *  Usage:
 *    <RequireRole allow={["admin", "inspector"]}>
 *      <StudioShell />
 *    </RequireRole>
 */
export function RequireRole({
  allow, children, fallback = "/atlas",
}: {
  allow: ReadonlyArray<"admin" | "inspector" | "engineer" | "client">;
  children: ReactNode;
  fallback?: string;
}): JSX.Element {
  // Read cached user — RequireAuth already verified the token exists.
  // We don't re-fetch /me here; if the cached role doesn't match, the
  // backend will still 403 on data calls — this guard is UX-only.
  const userRaw = typeof window !== "undefined"
    ? localStorage.getItem("xzynexa.user")
    : null;
  try {
    const u = userRaw ? JSON.parse(userRaw) as { role: string } : null;
    if (u && allow.includes(u.role as "admin" | "inspector" | "engineer" | "client")) {
      return <>{children}</>;
    }
  } catch { /* fall through to redirect */ }
  return <Navigate to={fallback} replace />;
}

export { errMsg };
